import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, Plus, Edit3, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { norm } from "@/lib/normalize";
import { useLookups } from "@/context/LookupContext";
import { format, parseISO } from "date-fns";
import { formatINR } from "@/lib/currency";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try { return format(parseISO(dateStr), "MMMM do, yyyy"); }
  catch { return dateStr; }
};

const EMPTY = { firstName: "", lastName: "", email: "", phone: "", gender: "", source: "", membershipPlanId: "", referredByStaffId: "" };

export default function Customers() {
  const { user } = useAuth();
  const { lookups } = useLookups();
  const isStaff = norm(user.roleName) === "admin" || norm(user.roleName) === "staff" || norm(user.roleName) === "receptionist";
  const isAdmin = norm(user.roleName) === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get("/customers").then((r) => setRows(r.data)).catch(() => toast.error("Failed"));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get("/staff").then((r) => setStaffList(r.data)).catch(() => {});
  }, []);

  // useEffect(() => {
  //   api.get("/customers").then((r) => setRows(r.data)).catch(() => toast.error("Failed"));
  // }, []);

  const filtered = rows.filter((r) => {
    const fullName = ((r.firstName || "") + " " + (r.lastName || "")).toLowerCase();
    const phone = (r.phone || "").toLowerCase();
    const search = q.toLowerCase();
    return fullName.includes(search) || phone.includes(search);
  });

  const goAppointmentsById = (id) => {
    const params = new URLSearchParams();
    if (id) params.set("appointment", id);
    const url = `/dashboard/appointments?${params.toString()}`;
    navigate(url);
  };

  const openAdd = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ firstName: c.firstName || "", lastName: c.lastName || "", email: c.email || "", phone: c.phone || "", gender: c.gender || "", source: c.source || "", membershipPlanId: c.membershipPlanId ? String(c.membershipPlanId) : "", walletBalance: c.walletBalance || 0, lastVisited: c.lastVisited || "", referredByStaffId: c.referredByStaffId ? String(c.referredByStaffId) : "" });
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim()) { toast.error("First Name required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, membershipPlanId: form.membershipPlanId ? Number(form.membershipPlanId) : null, referredByStaffId: form.referredByStaffId ? Number(form.referredByStaffId) : null, walletBalance: Number(form.walletBalance || 0) };
      if (editingId) {
        await api.put(`/customers/${editingId}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer added");
      }
      setOpen(false); setForm(EMPTY); setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="customers-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">CRM</div>
          <h1 className="font-heading text-4xl text-white">Customers</h1>
        </div>
        {isStaff && (
          <button data-testid="add-customer-btn" onClick={openAdd}
            className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Customer
          </button>
        )}
      </div>
      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-emerald-muted absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…"
          className="pl-9 bg-emerald-surface border-gold-subtle text-white" data-testid="customer-search" />
      </div>
      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-gold-subtle hover:bg-transparent">
              <TableHead className="text-emerald-muted">Name</TableHead>
              <TableHead className="text-emerald-muted">Phone</TableHead>
              <TableHead className="text-emerald-muted">Visits</TableHead>
              <TableHead className="text-emerald-muted">Last Visited</TableHead>
              <TableHead className="text-emerald-muted text-right">Appointments</TableHead>
              {isStaff && <TableHead className="text-emerald-muted w-24"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={isStaff ? 6 : 5} className="text-center text-emerald-muted py-8">No customers found.</TableCell></TableRow>}
            {filtered.map((c) => (
              <TableRow key={c.id} className="border-gold-subtle"
                data-testid={`customer-row-${c.id}`}>  
              <TableCell className="text-white">{[c.firstName, c.lastName].filter(Boolean).join(" ")}</TableCell>
                <TableCell className="text-emerald-muted">{c.phone || "—"}</TableCell>
                <TableCell className="text-emerald-muted">{c.numberOfVisits ?? "—"}</TableCell>
                <TableCell className="text-emerald-text">{formatDate(c.lastVisited)}</TableCell>
                <TableCell className="text-emerald-muted text-gold text-right">
                    {c.appointmentCount > 0 && (
                      <button onClick={() => goAppointmentsById(c.id)} data-testid={`go-to-appt-${c.id}`} className="text-emerald-muted-gold">{c.appointmentCount}</button>
                    )}
                </TableCell>
                {isStaff && (
                  <TableCell className="text-right">
                    <button onClick={() => openEdit(c)} className="text-emerald-muted hover:text-gold p-1"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => del(c.id)} className="text-emerald-muted hover:text-red-300 p-1 ml-2" data-testid={`delete-customer-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingId ? "Edit Customer" : "Add Customer"}
            </DialogTitle>
            <DialogDescription className="text-emerald-muted">
              {editingId ? "Update customer details." : "Create a new customer record."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  className="bg-emerald-elev border-gold-subtle mt-1" data-testid="customer-fname-input" required />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  className="bg-emerald-elev border-gold-subtle mt-1" data-testid="customer-lname-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-emerald-elev border-gold-subtle mt-1" data-testid="customer-email-input" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-emerald-elev border-gold-subtle mt-1" data-testid="customer-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={(val) => setForm({ ...form, gender: val })}>
                  <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9" data-testid="customer-gender-input">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MALE">Male</SelectItem>
                    <SelectItem value="FEMALE">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={(val) => setForm({ ...form, source: val, referredByStaffId: (val === "EMPLOYEE REFERRAL" || val === "EMPLOYEE_REFERRAL") ? form.referredByStaffId : "" })}>
                  <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9" data-testid="customer-source-input">
                    <SelectValue placeholder="How did they find us?" />
                  </SelectTrigger>
                  <SelectContent>
                    {(lookups?.customerSources || []).map((s) => (
                      <SelectItem key={s.name || s} value={s.name || s}>{s.name || s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(form.source === "EMPLOYEE REFERRAL" || form.source === "EMPLOYEE_REFERRAL") && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Referred By</Label>
                  <Select value={form.referredByStaffId} onValueChange={(val) => setForm({ ...form, referredByStaffId: val })}>
                    <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9" data-testid="customer-referred-staff-input">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {staffList.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>{s.firstName || s.name || [s.firstName, s.lastName].filter(Boolean).join(" ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {editingId && (
                  <div>
                    <Label>Last Visited</Label>
                    <Input value={formatDate(form.lastVisited)} disabled className="bg-emerald-elev border-gold-subtle mt-1 text-emerald-muted" data-testid="customer-last-visited" />
                  </div>
                )}
              </div>
            )}
            {!(form.source === "EMPLOYEE REFERRAL" || form.source === "EMPLOYEE_REFERRAL") && editingId && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Last Visited</Label>
                  <Input value={formatDate(form.lastVisited)} disabled className="bg-emerald-elev border-gold-subtle mt-1 text-emerald-muted" data-testid="customer-last-visited" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Membership Plan</Label>
                <Select value={form.membershipPlanId} onValueChange={(val) => setForm({ ...form, membershipPlanId: val })}>
                  <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9" data-testid="customer-membership-input">
                    <SelectValue placeholder="Select membership" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Membership</SelectItem>
                    {(lookups?.membershipPlans || []).map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.planName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editingId && (
                <div>
                  <Label>Wallet Balance (₹)</Label>
                  <Input type="number" step="1000" value={form.walletBalance ?? ""} 
                    onChange={(e) => setForm({ ...form, walletBalance: e.target.value })}
                    disabled={!isAdmin}
                    placeholder="Enter amount"
                    className="bg-emerald-elev border-gold-subtle mt-1 text-gold" data-testid="customer-wallet-balance" />
                </div>
              )}
            </div>
            <button type="submit" disabled={saving}
              className="gold-button w-full h-11 rounded-md disabled:opacity-60"
              data-testid="save-customer-btn">
              {saving ? "Saving…" : (editingId ? "Save Changes" : "Add Customer")}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
